import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FolderKanban,
  FlaskConical,
  BookOpen,
  CalendarDays,
  Award,
  Layers,
  RefreshCw,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { fetchStemProjects } from "../redux/stemThunk";

export function StemProjectsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projects = [], status, error } = useSelector(
    (state) => state.stem || {}
  );

  useEffect(() => {
    dispatch(fetchStemProjects(1));
  }, [dispatch]);

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        background: "var(--white)",
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid var(--line)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <FolderKanban size={20} color="var(--blue)" />
          <div>
            <h2
              style={{
                fontSize: "16px",
                fontWeight: "700",
                margin: 0,
                color: "var(--ink)",
              }}
            >
              STEM Projects
            </h2>
            <span style={{ fontSize: "11.5px", color: "var(--mute)" }}>
              Active projects for Category 1 (STEM) • Click any project to open stages
            </span>
          </div>
        </div>

        <button
          type="button"
          className="stem-refresh-btn"
          onClick={() => dispatch(fetchStemProjects(1))}
          disabled={status === "loading"}
          style={{
            background: "var(--white)",
            border: "1px solid var(--line)",
            borderRadius: "6px",
            padding: "5px 10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "11.5px",
            color: "var(--ink)",
          }}
        >
          <RefreshCw
            size={12}
            style={{
              animation:
                status === "loading" ? "spin 1s linear infinite" : "none",
            }}
          />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: "10px 12px",
            borderRadius: "8px",
            background: "var(--red-soft)",
            border: "1px solid var(--red-line)",
            color: "var(--red-ink)",
            fontSize: "12.5px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "14px",
          }}
        >
          <AlertCircle size={15} />
          <span>{error}</span>
        </div>
      )}

      {status === "loading" && projects.length === 0 && (
        <div
          style={{
            padding: "30px 20px",
            textAlign: "center",
            color: "var(--mute)",
            fontSize: "13px",
          }}
        >
          <RefreshCw
            size={18}
            color="var(--brand)"
            style={{
              margin: "0 auto 6px auto",
              display: "block",
              animation: "spin 1s linear infinite",
            }}
          />
          <span>Loading STEM projects...</span>
        </div>
      )}

      {projects.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "12px",
            marginTop: "12px",
          }}
        >
          {projects.map((proj) => {
            const stageCount = proj.stages?.length ?? proj._count?.stages ?? 0;

            return (
              <div
                key={proj.id}
                onClick={() => navigate(`/student-stem/projects/${proj.id}`)}
                style={{
                  border: "1px solid var(--line)",
                  borderRadius: "8px",
                  padding: "12px 14px",
                  background: "var(--white)",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--brand)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 3px 10px rgba(15, 23, 42, 0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--line)";
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "0 1px 2px rgba(15, 23, 42, 0.04)";
                }}
              >
                {/* Header row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "4px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "var(--ink)",
                      margin: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                    }}
                    title={proj.name}
                  >
                    {proj.name}
                  </h4>
                  <span
                    style={{
                      fontSize: "9.5px",
                      fontWeight: "600",
                      color: proj.status === "ACTIVE" ? "var(--green)" : "var(--mute)",
                      background: proj.status === "ACTIVE" ? "var(--green-soft)" : "var(--bg)",
                      padding: "1px 6px",
                      borderRadius: "10px",
                      flexShrink: 0,
                    }}
                  >
                    {proj.status || "ACTIVE"}
                  </span>
                </div>

                {/* Description snippet without stage names */}
                <p
                  style={{
                    fontSize: "11.5px",
                    color: "var(--mute)",
                    margin: "0 0 10px 0",
                    lineHeight: "1.3",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={proj.description || ""}
                >
                  {proj.description || "No description provided."}
                </p>

                {/* Footer bar */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingTop: "8px",
                    borderTop: "1px solid var(--bg)",
                    fontSize: "11px",
                    color: "var(--mute)",
                    marginTop: "auto",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      fontWeight: "600",
                      color: stageCount > 0 ? "var(--mute)" : "var(--mute)",
                    }}
                  >
                    <Layers size={12} color="var(--mute)" />
                    <span>{stageCount} Stages</span>
                  </div>

                  <div className="stem-open-link" style={{ display: "flex", alignItems: "center", gap: "3px", fontWeight: "600" }}>
                    <span>Open</span>
                    <ArrowRight size={12} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        status !== "loading" && (
          <div
            style={{
              textAlign: "center",
              padding: "30px 20px",
              color: "var(--mute)",
            }}
          >
            <FolderKanban
              size={30}
              color="var(--line)"
              style={{ margin: "0 auto 6px auto", display: "block" }}
            />
            <p
              style={{
                fontSize: "13px",
                fontWeight: "600",
                margin: "0 0 3px 0",
              }}
            >
              No active STEM projects found
            </p>
            <span style={{ fontSize: "11.5px" }}>
              Projects under category ID 1 will be displayed here once active.
            </span>
          </div>
        )
      )}
    </div>
  );
}

export function StemExperimentsPage() {
  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        background: "var(--white)",
        padding: "24px",
        borderRadius: "12px",
        border: "1px solid var(--line)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "16px",
        }}
      >
        <FlaskConical size={24} color="var(--blue-2)" />
        <h2
          style={{
            fontSize: "18px",
            fontWeight: "700",
            margin: 0,
            color: "var(--ink)",
          }}
        >
          Experiments & Virtual Labs
        </h2>
      </div>
      <p style={{ color: "var(--mute)", fontSize: "13.5px" }}>
        Interactive laboratory simulations, physics kits, chemistry experiments,
        and lab observations.
      </p>
    </div>
  );
}

export function StemModulesPage() {
  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        background: "var(--white)",
        padding: "24px",
        borderRadius: "12px",
        border: "1px solid var(--line)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "16px",
        }}
      >
        <BookOpen size={24} color="#8b5cf6" />
        <h2
          style={{
            fontSize: "18px",
            fontWeight: "700",
            margin: 0,
            color: "var(--ink)",
          }}
        >
          STEM Learning Modules
        </h2>
      </div>
      <p style={{ color: "var(--mute)", fontSize: "13.5px" }}>
        Core concepts covering Coding, Robotics, AI, Electronics, and
        Mathematics tutorials.
      </p>
    </div>
  );
}

export function StemActivitiesPage() {
  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        background: "var(--white)",
        padding: "24px",
        borderRadius: "12px",
        border: "1px solid var(--line)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "16px",
        }}
      >
        <CalendarDays size={24} color="#f59e0b" />
        <h2
          style={{
            fontSize: "18px",
            fontWeight: "700",
            margin: 0,
            color: "var(--ink)",
          }}
        >
          STEM Activities & Schedule
        </h2>
      </div>
      <p style={{ color: "var(--mute)", fontSize: "13.5px" }}>
        Upcoming science fairs, hackathons, STEM workshops, and classroom
        challenges.
      </p>
    </div>
  );
}

export function StemCertificatesPage() {
  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        background: "var(--white)",
        padding: "24px",
        borderRadius: "12px",
        border: "1px solid var(--line)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "16px",
        }}
      >
        <Award size={24} color="var(--green)" />
        <h2
          style={{
            fontSize: "18px",
            fontWeight: "700",
            margin: 0,
            color: "var(--ink)",
          }}
        >
          STEM Achievements & Certificates
        </h2>
      </div>
      <p style={{ color: "var(--mute)", fontSize: "13.5px" }}>
        View and download your earned badges and STEM milestone completion
        certificates.
      </p>
    </div>
  );
}
